import { DeviceService } from './device.service';
import { inject, Injectable, signal } from '@angular/core';
import {
  Auth,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  deleteUser,
  GoogleAuthProvider,
  OAuthProvider,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInAnonymously,
  signInWithCredential,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  User,
} from '@angular/fire/auth';
import { WindowService } from '../utils/services/window.service';
import { Router } from '@angular/router';
import {
  GoogleLoginResponseOnline,
  SocialLogin,
} from '@capgo/capacitor-social-login';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class FireAuthService {
  private deviceService = inject(DeviceService);
  public fireAuthToken = signal<string>('');
  public user = signal<User | null>(null);
  public authStateMessage = signal<String>('');
  env = environment;

  windowService = inject(WindowService);

  constructor(
    private fireAuth: Auth,
    private router: Router,
  ) {
    this.fireAuth.onAuthStateChanged(async (user) => {
      if (user) {
        this.user.set(user);
        this.setFireAuthToken(user);
      } else {
        this.user.set(null);
        this.clearFireAuthToken();
      }
    });
  }

  // Utility functions

  async initializeSocialAuth() {
    return SocialLogin.initialize({
      google: {
        webClientId: this.env.webClientId,
        iOSClientId: this.env.iOSWebClientId,
        mode: 'online',
      },
    });
  }

  async setFireAuthToken(user: User) {
    const token = await user.getIdToken();
    if (!token) return;
    this.fireAuthToken.set(token);
  }

  clearFireAuthToken() {
    this.fireAuthToken.set('');
  }

  // Anonymous user functions

  async signInAnonymously() {
    try {
      if (this.fireAuthToken()) return;
      const result = await signInAnonymously(this.fireAuth);
      this.setFireAuthToken(result.user);
      return result;
    } catch (error: any) {
      throw this.authErrorHandler(error.code) as string;
    }
  }

  upgradeAnonymousUserAccount(provider: string) {
    // const auth = this.auth;
    // // https://firebase.google.com/docs/auth/web/anonymous-auth
    // // Obtain credential with users verificationCode.
    // const authCredential = PhoneAuthProvider.credential(verificationId, verificationCode);
    // const credential = EmailAuthProvider.credential(email, password);
    // const credential = GoogleAuthProvider.credential( googleUser.getAuthResponse().id_token);
    // // Upgrade User account user
    // return linkWithCredential(auth.currentUser!, authCredential)
  }

  // EMAIL AUTH FUNCTIONS

  async loginWithEmailAndPassword(credentials: any) {
    return await signInWithEmailAndPassword(
      this.fireAuth,
      credentials.email,
      credentials.password,
    );
  }

  async signUpWithEmailAndPassword(credentials: any) {
    return await createUserWithEmailAndPassword(
      this.fireAuth,
      credentials.email,
      credentials.password,
    );
  }

  async sendResetPasswordEmail(email: any) {
    this.fireAuth.useDeviceLanguage();
    var actionCodeSettings = {
      url: `${environment.appUrl}?email=${email}`,
    };
    return await sendPasswordResetEmail(
      this.fireAuth,
      email,
      actionCodeSettings,
    );
  }

  async resetPassword(code: any, password: string) {
    return await confirmPasswordReset(this.fireAuth, code, password);
  }

  verifyEmail() {
    const settings = {
      url: this.windowService.windowRef.location.href + '?results=true',
    };

    this.fireAuth.useDeviceLanguage();
    return sendEmailVerification(this.fireAuth.currentUser!, settings);
  }

  // CUSTOM AUTH FUNCTIONS

  async customLogin(token: any) {
    try {
      const result = await signInWithCustomToken(this.fireAuth, token);
      const credential = await result.user.getIdToken();
      this.fireAuthToken.set(credential ?? '');
      return result;
    } catch (error: any) {
      throw this.authErrorHandler(error.code) as string;
    }
  }

  // async addUserPhoneNumber(phone: any) {
  //   try {
  //     return await updateProfile(this.fireAuth.currentUser!, { phoneNumber: phone });
  //   } catch (error: any) {
  //     throw this.authErrorHandler(error.code) as string;
  //   }
  // }

  // SSO AUTH FUNCTIONS

  // async continueWithProvider(provider: string): Promise<void> {
  //   switch (provider) {
  //     case 'google':
  //       return this.continueWithGoogle();
  //     case 'apple':
  //       return this.continueWithApple();
  //     default:
  //       throw new Error('Invalid provider');
  //   }
  // }

  async continueWithGoogle() {
    if (this.deviceService.isMobile && !environment.useEmulators) {
      await this.initializeSocialAuth();
      const authResponse = await SocialLogin.login({
        provider: 'google',
        options: {
          scopes: ['email', 'profile'],
        },
      });

      const { result } = authResponse;

      const { idToken } = result as GoogleLoginResponseOnline;
      const credential = GoogleAuthProvider.credential(idToken);

      if (credential) {
        return signInWithCredential(this.fireAuth, credential!);
      } else {
        return null;
      }
    }

    const provider = new GoogleAuthProvider();
    provider.addScope('profile');
    provider.addScope('email');
    return signInWithPopup(this.fireAuth, provider);
  }

  async continueWithApple() {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    // if (this.deviceService.isMobile()) {
    //   signInWithRedirect(this.fireAuth, provider);
    //   return getRedirectResult(this.fireAuth);
    // }
    return signInWithPopup(this.fireAuth, provider);
  }

  // SIGNOUT FUNCTIONS

  async signOut() {
    try {
      await SocialLogin.logout({ provider: 'google' });
      await signOut(this.fireAuth);
      this.fireAuthToken.set('');
      this.router.navigateByUrl('/auth/login');
    } catch (error: any) {
      throw this.authErrorHandler(error.code) as string;
    }
  }

  deleteUser() {
    if (!this.fireAuth.currentUser) return;
    return deleteUser(this.fireAuth.currentUser);
  }

  // ERROR HANDLING FUNCTIONS

  authErrorHandler(error: any) {
    const { code } = error;

    // console.log('error', error);
    switch (code) {
      case 'auth/expired-action-code':
        return 'Sorry, the credential for this action has expired. Please, request a new code.';
      case 'auth/invalid-action-code':
        return 'Sorry, we were unable to validate your identity for this action. Please, make a new password reset request.';
      case 'auth/user-disabled':
        return 'Sorry, your account has been disabled.';
      case 'auth/user-not-found':
        return 'Sorry, this user does not exist with the given credentials. Create account Instead.';
      case 'auth/weak-password':
        return 'Sorry, your new password is not strong enough, Try again.';
      case 'auth/email-already-in-use':
        return 'Sorry, an account already exists with the given credentials. Login Instead.';
      case 'auth/invalid-email':
        return 'Sorry, your email address is not valid.';
      case 'auth/operation-not-allowed':
        return 'Sorry, this operation is not allowed!';
      case 'auth/account-exists-with-different-credential':
        return 'Sorry, Please login with one of the different methods.';
      case 'auth/auth-domain-config-required':
        return 'Sorry, you access is unauthorized!';
      case 'auth/credential-already-in-use':
        return 'Sorry, this credential is already in use.';
      case 'auth/timeout':
        return 'Sorry, you access is unauthorized!';
      case 'auth/missing-continue-uri':
        return 'A continue URL must be provided in the request.';
      case 'auth/invalid-continue-uri':
        return 'Sorry, the continue URL provided in the request is invalid.';
      case 'auth/unauthorized-continue-uri':
        return 'Unauthorized continue URL provided in the request.';
      case 'auth/invalid-credential':
        return 'Sorry, invalid or expired credentials. Please try again.';
      case 'auth/wrong-password':
        return 'Sorry, invalid password entered. Please try again.';
      case 'auth/invalid-verification-code':
        return 'Sorry, the verification code was not valid. Please check the code and try again.';
      case 'auth/invalid-verification-id':
        return 'Sorry, the verification ID of the credential is not valid. Please try again.';
      case 'auth/missing-verification-id':
        return 'Sorry, the verification ID of the credential is not valid. Please try again.';
      case 'auth/captcha-check-failed':
        return 'Sorry, Captcha token was invalid or expired.';
      case 'auth/invalid-phone-number':
        return 'Sorry, the phone number you entered is not valid. Please update and retry.';
      case 'auth/cancelled-popup-request':
        return 'Sorry, you have multiple popup operations triggered. Only one popup request is allowed at one time. Please close the others.';
      case 'auth/operation-not-supported-in-this-environment':
        return 'Sorry, this request is not secure.';
      case 'auth/popup-blocked':
        return 'Sorry, the popup was blocked by the browser. Please try again.';
      case 'auth/popup-closed-by-user':
        return 'You canceled the popup operation. Please try again.';
      case 'auth/unauthorized-domain':
        return 'Sorry, this app domain is not authorized.';
      case 'auth/invalid-user-token':
        return 'Sorry, user does not exist on this project.';
      case 'auth/tenant-id-mismatch':
        return 'Sorry, session ID mismatch.';
      case 'auth/user-token-expired':
        return 'Sorry, your session has expired.';
      case 'auth/null-user':
        return 'Sorry, your session has expired.';
      case 'auth/id-token-expired':
        this.signOut();
        return 'Sorry, your session has expired.';
      case 'auth/requires-recent-login':
        return 'You need to Re-authenticate to perform this action, please logout, login and try again.';
      default:
        return (
          error?.message ??
          'We are having trouble processing your request at this time, please try again later'
        );
    }
  }
}
